<script setup>

import NumberOnlyInput from "@/components/common/input/NumberOnlyInput"
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown"
import Container from "@/classes/component-classes/Container";
import ContainerExtended from "@/classes/component-classes/ContainerExtended";

import {useGetValidatedFields} from "@/composables/common/useGetValidatedFields";

import {ref, onMounted, nextTick, inject, computed} from 'vue';

const localArray = ref([]);

const emits = defineEmits(['entry-update'])

const props = defineProps({

  modelValue: {
    type: Array, default: function () {
      return []
    },
  },

  requiredFields: {type: Array, default: []},
  validationFail: Boolean,
  containers: {type: Array, default: []},

})

function addContainer(line, index) {

  let valid = validate(line);

  if (valid) {

    line.showAddButton = false;
    line.readOnly = true;

    localArray.value.push(new ContainerExtended());
    props.modelValue.push(new Container());

    modelValueUpdate(line, index);

  }

}

function validate(line) {

  if(!line.containerType || !line.teu || !line.containerNumber || !line.sealNumber) return false;

  return true;

}

function modelValueUpdate(line, index) {

  // Collect the pure model keys and update the values from the current local line
  for (const propName in props.modelValue[0]) {
    props.modelValue[index][propName] = line[propName]
  }

}

function prepareDelete(line) {
  line.isDeleting = true;
}

function cancelDelete(line) {
  line.isDeleting = false;
}

function removeContainer(index, line) {

  localArray.value.splice(index, 1);
  props.modelValue.splice(index, 1);

  localArray.value[localArray.value.length - 1].isEditing = false;
  localArray.value[localArray.value.length - 1].readOnly = false;

}

function editContainer(line) {

  localArray.value.forEach(item => {
    item.readOnly = true;
  });

  line.readOnly = false;
  line.isEditing = true;

  // Take the add button away if editing a line
  localArray.value[localArray.value.length - 1].isEditing = true;

}

function acceptEdit(line, index) {

  let valid = validate(line)

  if (valid) {

    if (index === localArray.value.length - 1) {
      props.modelValue.push(new Container())
    }

    modelValueUpdate(line, index);

    line.readOnly = true;
    localArray.value[localArray.value.length - 1].isEditing = false;
    localArray.value[localArray.value.length - 1].readOnly = false;
  }

}

onMounted(() => {

  // Set memory reference from local data
  let containers = localArray.value

  props.modelValue.forEach(function (item) {

    // Cast the pure model class into the local extended class
    let container = {...new ContainerExtended(), ...item}

    // Set all correct props for returning data for button actions
    container.showAddButton = false;
    container.readOnly = true;

    containers.push(container)
  })

  // Add a new empty line after the current container
  containers.push(new ContainerExtended())

})

</script>

<template>
  <div class="cmp-container-multi-entry">
    <div class="cmp-container-multi-entry_labels">
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'containerType')}">Container Type</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'teu')}" style="font-size: 12px">TEU</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'containerNumber')}">Container No.</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'sealNumber')}">SealNumber</div>
    </div>
    <div class="cmp-container-multi-entry_container">
      <div v-for="(line, index) in localArray" class="cmp-container-multi-entry_wrapper" :key="index">
        <MultiSearchDropDown
            :width="240"
            placeholder="Select or Search...."
            :clearable="!line.readOnly"
            :allowCustomValue="false"
            :selectPrimitiveValue="true"
            :disableFilter="false"
            @select="line.teu = $event.teu.toString()"
            v-model="line.containerType"
            :disable="line.readOnly"
            :data-source="props.containers">
        </MultiSearchDropDown>
        <NumberOnlyInput
            v-model="line.teu"
            :allowDecimal="true"
            :readonly="true"
            :width="20">
        </NumberOnlyInput>
        <NumberOnlyInput
            v-model="line.containerNumber"
            :allowDecimal="true"
            :readonly="line.readOnly"
            :width="115">
        </NumberOnlyInput>
        <NumberOnlyInput
            v-model="line.sealNumber"
            :allowDecimal="true"
            :readonly="line.readOnly"
            :width="115">
        </NumberOnlyInput>

        <div v-if="line.showAddButton && !line.isEditing" class="cmp-container-multi-entry_icons" title="Add New Line">
          <img @click="addContainer(line, index)" height="20" width="20" src="@/assets/icons/akar/akar-icons--circle-plus.png">
        </div>
        <div v-if="line.readOnly && !line.isDeleting" class="cmp-container-multi-entry_icons" title="Edit Line">
          <img @click="editContainer(line)" height="20" width="20" src="@/assets/icons/akar/akar-icons--edit.png">
        </div>
        <div v-if="line.isDeleting" class="cmp-container-multi-entry_icons" title="Confirm Delete">
          <img @click="removeContainer(index, line)" height="20" width="20" src="@/assets/icons/akar/akar-icons--check.png">
        </div>
        <div v-if="!line.readOnly && line.isEditing && !line.isDeleting" class="cmp-container-multi-entry_icons" title="Accept Changes">
          <img @click="acceptEdit(line, index)" height="20" width="20" src="@/assets/icons/akar/akar-icons--check.png">
        </div>
        <div v-if="!line.showAddButton && !line.isDeleting" class="cmp-container-multi-entry_icons" title="Delete">
          <img @click="prepareDelete(line)" height="18" width="18" src="@/assets/icons/akar/akar-icons--trash-can.png">
        </div>
        <div v-if="line.isDeleting" class="cmp-container-multi-entry_icons" title="Do Not Delete">
          <img @click="cancelDelete(line)" height="24" width="24" src="@/assets/icons/akar/akar-icons--x-small.png">
        </div>
      </div>
    </div>
  </div>

</template>


<style lang="scss" scoped>

.cmp-container-multi-entry {

  &_wrapper {
    width: 940px;
    display: flex;
    gap: 4px;
  }

  &_wrapper > div {
    margin-bottom: 3px;
  }

  &_container {
    height: 145px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &_labels {
    margin-bottom: 3px;
    font-size: 13px;
    display: grid;
    grid-template-columns: 243px 28px 119px 120px;

  }

  &_labels div {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    color: #2b2b2b;
    font-size: 13px;
    font-weight: bold;
    display: flex;
  }

  &_icons {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 18px;
    margin-left: 10px;
  }

}

</style>
